import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DuplicateWarningDialog from '../components/common/DuplicateWarningDialog';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { SearchableSelect, MEXICAN_STATES, COUNTRIES } from '../components/SearchableSelect';
import {
  Users, Plus, Search, Edit, Trash2, Eye, Building2,
  Mail, Phone, MapPin, ArrowLeftRight,
  Loader2, AlertTriangle, Calendar, User, Package, X, BarChart3,
  Filter, CheckCircle, XCircle, Clock
} from 'lucide-react';

const ClientsPage = () => {
  const { api, hasPermission, isSuperAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientEvents, setClientEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('clients');
  const [eventHistory, setEventHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    client_id: '', event_city: '', event_state: '', event_country: '',
    date_from: '', date_to: '', movement_type: ''
  });
  const [listFilters, setListFilters] = useState({ country: '', state: '', city: '', sortEvent: '', minEvents: '' });

  const emptyForm = {
    name: '', company: '', contact_name: '', email: '',
    phone: '', address: '', city: '', state: '', country: '', notes: '',
    contacts: []
  };
  const [form, setForm] = useState(emptyForm);

  const canCreate = isSuperAdmin() || hasPermission('create_clients');
  const canEdit = isSuperAdmin() || hasPermission('edit_clients');
  const canDelete = isSuperAdmin() || hasPermission('delete_clients');

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api().get('/clients');
      setClients(res.data || []);
    } catch {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const loadReport = async () => {
    setReportLoading(true);
    try {
      const res = await api().get('/clients/report/monthly');
      setReportData(res.data);
    } catch {
      toast.error('Error al cargar reporte');
    } finally {
      setReportLoading(false);
    }
  };

  const loadClientEvents = async (clientId) => {
    setEventsLoading(true);
    try {
      const res = await api().get(`/clients/${clientId}/events`);
      setClientEvents(res.data || []);
    } catch {
      setClientEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const loadEventHistory = useCallback(async (filters = historyFilters) => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api().get(`/clients/events/history?${params.toString()}`);
      setEventHistory(res.data?.events || []);
    } catch {
      toast.error('Error al cargar historial');
      setEventHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [api, historyFilters]);

  const filteredClients = (() => {
    let result = clients.filter(c => {
      const term = searchTerm.toLowerCase();
      const matchesSearch = c.name?.toLowerCase().includes(term) ||
        c.company?.toLowerCase().includes(term) ||
        c.contact_name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.city?.toLowerCase().includes(term) ||
        c.state?.toLowerCase().includes(term) ||
        c.country?.toLowerCase().includes(term) ||
        (c.contacts || []).some(ct =>
          ct.name?.toLowerCase().includes(term) ||
          ct.email?.toLowerCase().includes(term) ||
          ct.phone?.toLowerCase().includes(term) ||
          ct.city?.toLowerCase().includes(term) ||
          ct.state?.toLowerCase().includes(term) ||
          ct.country?.toLowerCase().includes(term)
        );
      if (!matchesSearch) return false;
      if (listFilters.country) {
        const allCountries = [c.country, ...(c.contacts || []).map(ct => ct.country)].filter(Boolean).map(x => x.toLowerCase());
        if (!allCountries.some(x => x.includes(listFilters.country.toLowerCase()))) return false;
      }
      if (listFilters.state) {
        const allStates = [c.state, ...(c.contacts || []).map(ct => ct.state)].filter(Boolean).map(x => x.toLowerCase());
        if (!allStates.some(x => x.includes(listFilters.state.toLowerCase()))) return false;
      }
      if (listFilters.city) {
        const allCities = [c.city, ...(c.contacts || []).map(ct => ct.city)].filter(Boolean).map(x => x.toLowerCase());
        if (!allCities.some(x => x.includes(listFilters.city.toLowerCase()))) return false;
      }
      if (listFilters.minEvents) {
        const min = parseInt(listFilters.minEvents);
        if (!isNaN(min) && (c.event_count || 0) < min) return false;
      }
      return true;
    });
    if (listFilters.sortEvent === 'asc') {
      result.sort((a, b) => new Date(a.last_event_date || 0) - new Date(b.last_event_date || 0));
    } else if (listFilters.sortEvent === 'desc') {
      result.sort((a, b) => new Date(b.last_event_date || 0) - new Date(a.last_event_date || 0));
    } else if (listFilters.sortEvent === 'most') {
      result.sort((a, b) => (b.event_count || 0) - (a.event_count || 0));
    } else if (listFilters.sortEvent === 'least') {
      result.sort((a, b) => (a.event_count || 0) - (b.event_count || 0));
    }
    return result;
  })();

  const validateEmail = (email) => {
    if (!email) return true; // empty is ok, not required
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return true;
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 7;
  };

  const validateForm = () => {
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return false; }
    if (form.email && !validateEmail(form.email)) {
      toast.error('El email principal no tiene formato valido (ej: correo@ejemplo.com)'); return false;
    }
    if (form.phone && !validatePhone(form.phone)) {
      toast.error('El telefono principal debe tener al menos 7 digitos'); return false;
    }
    for (let i = 0; i < form.contacts.length; i++) {
      const ct = form.contacts[i];
      if (ct.email && !validateEmail(ct.email)) {
        toast.error(`Contacto #${i + 1}: email no tiene formato valido`); return false;
      }
      if (ct.phone && !validatePhone(ct.phone)) {
        toast.error(`Contacto #${i + 1}: telefono debe tener al menos 7 digitos`); return false;
      }
    }
    return true;
  };

  const getClientCompleteness = (client) => {
    const missing = [];
    if (!client.contact_name && !(client.contacts?.length > 0 && client.contacts.some(c => c.name))) missing.push('Contacto');
    if (!client.email && !(client.contacts?.length > 0 && client.contacts.some(c => c.email))) missing.push('Email');
    if (!client.phone && !(client.contacts?.length > 0 && client.contacts.some(c => c.phone))) missing.push('Telefono');
    if (!client.city && !(client.contacts?.length > 0 && client.contacts.some(c => c.city))) missing.push('Ciudad');
    if (!client.state && !(client.contacts?.length > 0 && client.contacts.some(c => c.state))) missing.push('Estado');
    return missing;
  };

  const [duplicateError, setDuplicateError] = useState(null);

  const performCreateClient = async (forceCreate = false) => {
    const url = forceCreate ? '/clients?force_create=true' : '/clients';
    return api().post(url, form);
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      await performCreateClient(false);
      toast.success('Cliente creado');
      setIsCreateOpen(false);
      setForm(emptyForm);
      loadClients();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.duplicate) {
        setDuplicateError(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Error al crear cliente');
      }
    } finally { setSaving(false); }
  };

  const handleForceCreateClient = async () => {
    setDuplicateError(null);
    setSaving(true);
    try {
      await performCreateClient(true);
      toast.success('Cliente creado (duplicado forzado)');
      setIsCreateOpen(false);
      setForm(emptyForm);
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear cliente');
    } finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      await api().put(`/clients/${selectedClient.id}`, form);
      toast.success('Cliente actualizado');
      setIsEditOpen(false);
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api().delete(`/clients/${selectedClient.id}`);
      toast.success('Cliente eliminado');
      setIsDeleteOpen(false);
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally { setSaving(false); }
  };

  const openView = (client) => {
    setSelectedClient(client);
    setIsViewOpen(true);
    loadClientEvents(client.id);
  };

  const openEdit = (client) => {
    setSelectedClient(client);
    setForm({
      name: client.name || '', company: client.company || '',
      contact_name: client.contact_name || '', email: client.email || '',
      phone: client.phone || '', address: client.address || '',
      city: client.city || '', state: client.state || '', country: client.country || '',
      notes: client.notes || '',
      contacts: client.contacts || []
    });
    setIsEditOpen(true);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const addContact = () => {
    setForm({ ...form, contacts: [...form.contacts, { name: '', email: '', phone: '', city: '', state: '', country: '' }] });
  };

  const removeContact = (idx) => {
    setForm({ ...form, contacts: form.contacts.filter((_, i) => i !== idx) });
  };

  const updateContact = (idx, field, value) => {
    const updated = [...form.contacts];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, contacts: updated });
  };

  const clientFormFields = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nombre del Cliente *</Label>
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej: UNAM" data-testid="client-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Empresa / Organizacion</Label>
          <Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Ej: Universidad Nacional" data-testid="client-company-input" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Contacto Principal</Label>
          <Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="Persona de contacto" data-testid="client-contact-input" />
        </div>
        <div className="space-y-2">
          <Label>Email Principal</Label>
          <Input
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="correo@ejemplo.com"
            data-testid="client-email-input"
            className={form.email && !validateEmail(form.email) ? 'border-red-500 focus-visible:ring-red-500' : ''}
          />
          {form.email && !validateEmail(form.email) && (
            <p className="text-xs text-red-500">Formato invalido (ej: correo@ejemplo.com)</p>
          )}
        </div>
        <div className="space-y-2">
          <Label>Telefono Principal</Label>
          <Input
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            placeholder="555-1234"
            data-testid="client-phone-input"
            className={form.phone && !validatePhone(form.phone) ? 'border-red-500 focus-visible:ring-red-500' : ''}
          />
          {form.phone && !validatePhone(form.phone) && (
            <p className="text-xs text-red-500">Min 7 digitos</p>
          )}
        </div>
      </div>

      {/* Additional Contacts */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Contactos Adicionales ({form.contacts.length})</Label>
          <Button type="button" variant="outline" size="sm" onClick={addContact} data-testid="add-contact-btn">
            <Plus className="h-3 w-3 mr-1" /> Agregar Contacto
          </Button>
        </div>
        {form.contacts.map((ct, idx) => (
          <div key={idx} className="flex gap-2 items-start p-2 border rounded-lg bg-muted/20">
            <div className="grid grid-cols-3 gap-2 flex-1">
              <Input placeholder="Nombre" value={ct.name || ''} onChange={e => updateContact(idx, 'name', e.target.value)} className="h-8 text-xs" />
              <div>
                <Input
                  placeholder="Email"
                  value={ct.email || ''}
                  onChange={e => updateContact(idx, 'email', e.target.value)}
                  className={`h-8 text-xs ${ct.email && !validateEmail(ct.email) ? 'border-red-500' : ''}`}
                />
                {ct.email && !validateEmail(ct.email) && <p className="text-[10px] text-red-500 mt-0.5">Email invalido</p>}
              </div>
              <div>
                <Input
                  placeholder="Telefono"
                  value={ct.phone || ''}
                  onChange={e => updateContact(idx, 'phone', e.target.value)}
                  className={`h-8 text-xs ${ct.phone && !validatePhone(ct.phone) ? 'border-red-500' : ''}`}
                />
                {ct.phone && !validatePhone(ct.phone) && <p className="text-[10px] text-red-500 mt-0.5">Min 7 digitos</p>}
              </div>
              <Input placeholder="Ciudad" value={ct.city || ''} onChange={e => updateContact(idx, 'city', e.target.value)} className="h-8 text-xs" />
              <SearchableSelect
                value={ct.state || ''}
                onValueChange={v => updateContact(idx, 'state', v)}
                options={MEXICAN_STATES}
                placeholder="Estado"
                showManualOption={true}
                manualLabel="Agregar manualmente"
                size="sm"
              />
              <SearchableSelect
                value={ct.country || ''}
                onValueChange={v => updateContact(idx, 'country', v)}
                options={COUNTRIES}
                placeholder="Pais"
                showManualOption={true}
                manualLabel="Agregar manualmente"
                size="sm"
              />
            </div>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => removeContact(idx)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Ciudad Principal</Label>
          <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="Ciudad" data-testid="client-city-input" />
        </div>
        <div className="space-y-2">
          <Label>Estado</Label>
          <SearchableSelect
            value={form.state}
            onValueChange={v => setForm({ ...form, state: v })}
            options={MEXICAN_STATES}
            placeholder="Estado"
            showManualOption={true}
            manualLabel="Agregar manualmente"
            data-testid="client-state-select"
          />
        </div>
        <div className="space-y-2">
          <Label>Pais</Label>
          <SearchableSelect
            value={form.country || 'Mexico'}
            onValueChange={v => setForm({ ...form, country: v })}
            options={COUNTRIES}
            placeholder="Pais"
            showManualOption={true}
            manualLabel="Agregar manualmente"
            data-testid="client-country-select"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Direccion</Label>
        <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Calle, numero, colonia..." />
      </div>
      <div className="space-y-2">
        <Label>Notas</Label>
        <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notas adicionales..." rows={2} />
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            Clientes
          </h1>
          <p className="text-muted-foreground text-sm">Gestiona la informacion de tus clientes y su historial de eventos</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowReport(!showReport); if (!showReport) loadReport(); }} data-testid="client-report-btn">
              <BarChart3 className="h-4 w-4 mr-2" />
              {showReport ? 'Ver Clientes' : 'Reporte'}
            </Button>
            <Button onClick={() => { setForm(emptyForm); setIsCreateOpen(true); }} data-testid="create-client-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Cliente
            </Button>
          </div>
        )}
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, empresa, contacto, email, ciudad..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="client-search-input"
          />
        </div>
        <div className="min-w-[140px]">
          <SearchableSelect
            value={listFilters.country}
            onValueChange={v => setListFilters({...listFilters, country: v})}
            options={COUNTRIES}
            placeholder="Filtrar pais"
            showManualOption={true}
            manualLabel="Escribir pais"
            size="sm"
            data-testid="client-filter-country"
          />
        </div>
        <div className="min-w-[140px]">
          <SearchableSelect
            value={listFilters.state}
            onValueChange={v => setListFilters({...listFilters, state: v})}
            options={MEXICAN_STATES}
            placeholder="Filtrar estado"
            showManualOption={true}
            manualLabel="Escribir estado"
            size="sm"
            data-testid="client-filter-state"
          />
        </div>
        <Input
          className="h-9 w-[130px] text-sm"
          placeholder="Filtrar ciudad"
          value={listFilters.city}
          onChange={e => setListFilters({...listFilters, city: e.target.value})}
          data-testid="client-filter-city"
        />
        <Input
          className="h-9 w-[100px] text-sm"
          type="number"
          min="0"
          placeholder="Min eventos"
          value={listFilters.minEvents}
          onChange={e => setListFilters({...listFilters, minEvents: e.target.value})}
          data-testid="client-filter-min-events"
        />
        <Select value={listFilters.sortEvent} onValueChange={v => setListFilters({...listFilters, sortEvent: v === '_none' ? '' : v})}>
          <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="client-filter-sort">
            <SelectValue placeholder="Ordenar eventos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">Sin orden</SelectItem>
            <SelectItem value="desc">Ultimo evento (reciente)</SelectItem>
            <SelectItem value="asc">Ultimo evento (antiguo)</SelectItem>
            <SelectItem value="most">Mas eventos primero</SelectItem>
            <SelectItem value="least">Menos eventos primero</SelectItem>
          </SelectContent>
        </Select>
        {(listFilters.country || listFilters.state || listFilters.city || listFilters.sortEvent || listFilters.minEvents) && (
          <Button variant="ghost" size="sm" className="h-9" onClick={() => setListFilters({ country: '', state: '', city: '', sortEvent: '', minEvents: '' })} data-testid="client-filter-clear">
            <X className="h-3 w-3 mr-1" /> Limpiar
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{clients.length}</p>
              <p className="text-xs text-muted-foreground">Total Clientes</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Users className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{clients.filter(c => c.is_active).length}</p>
              <p className="text-xs text-muted-foreground">Activos</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <ArrowLeftRight className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{clients.reduce((sum, c) => sum + (c.event_count || 0), 0)}</p>
              <p className="text-xs text-muted-foreground">Total Eventos</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={v => { setActiveTab(v); if (v === 'history' && eventHistory.length === 0) loadEventHistory(); }}>
        <TabsList>
          <TabsTrigger value="clients" data-testid="tab-clients">Clientes</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historial de Eventos</TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="space-y-4 mt-4">
      {/* Report Section */}
      {showReport && (
        <Card>
          <CardContent className="p-4 md:p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Reporte por Cliente
            </h2>
            {reportLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            ) : !reportData || reportData.report.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No hay datos para el reporte</p>
            ) : (
              <div className="space-y-4">
                {reportData.report.map(r => (
                  <div key={r.client_id} className="border rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{r.client_name}</h3>
                        {r.company && <p className="text-xs text-muted-foreground">{r.company}</p>}
                      </div>
                      <div className="flex gap-3 text-center">
                        <div>
                          <p className="text-2xl font-bold text-primary">{r.total_events}</p>
                          <p className="text-xs text-muted-foreground">Eventos</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{r.total_items_moved}</p>
                          <p className="text-xs text-muted-foreground">Equipos</p>
                        </div>
                      </div>
                    </div>
                    {r.top_equipment.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Equipo mas solicitado:</p>
                        <div className="flex flex-wrap gap-1">
                          {r.top_equipment.map((eq, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {eq.name} ({eq.count})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      {r.last_event_date && <span>Ultimo evento: {formatDate(r.last_event_date)}</span>}
                      {r.first_event_date && <span>Primer evento: {formatDate(r.first_event_date)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Contacto(s)</TableHead>
                  <TableHead>Email(s)</TableHead>
                  <TableHead>Telefono(s)</TableHead>
                  <TableHead>Ciudad(es)</TableHead>
                  <TableHead>Estado(s)</TableHead>
                  <TableHead>Pais(es)</TableHead>
                  <TableHead className="whitespace-nowrap">Eventos</TableHead>
                  <TableHead className="whitespace-nowrap">Ultimo Evento</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={11}><Skeleton className="h-10 w-full" /></TableCell>
                    </TableRow>
                  ))
                ) : filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-12">
                      <Building2 className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                      <p className="text-muted-foreground">{searchTerm ? 'Sin resultados' : 'No hay clientes registrados'}</p>
                      {canCreate && !searchTerm && (
                        <Button variant="outline" size="sm" className="mt-3" onClick={() => { setForm(emptyForm); setIsCreateOpen(true); }}>
                          <Plus className="h-4 w-4 mr-2" /> Agregar primer cliente
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map(client => {
                    const allContacts = [
                      ...(client.contact_name ? [{ name: client.contact_name, email: client.email, phone: client.phone, city: client.city, state: client.state, country: client.country }] : []),
                      ...(client.contacts || [])
                    ];
                    const allEmails = [...new Set(allContacts.map(c => c.email).filter(Boolean))];
                    const allPhones = [...new Set(allContacts.map(c => c.phone).filter(Boolean))];
                    const allCities = [...new Set([client.city, ...(client.contacts || []).map(c => c.city)].filter(Boolean))];
                    const allStates = [...new Set([client.state, ...(client.contacts || []).map(c => c.state)].filter(Boolean))];
                    const allCountries = [...new Set([client.country, ...(client.contacts || []).map(c => c.country)].filter(Boolean))];
                    const allNames = [...new Set(allContacts.map(c => c.name).filter(Boolean))];

                    return (
                    <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-1.5">
                          <span>{client.name}</span>
                          {(() => {
                            const missing = getClientCompleteness(client);
                            if (missing.length === 0) return null;
                            return (
                              <span className="relative group" data-testid={`client-warning-${client.id}`}>
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-500 cursor-help" />
                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-50 whitespace-nowrap bg-popover border border-border text-popover-foreground text-[10px] px-2 py-1.5 rounded-md shadow-md">
                                  Falta: {missing.join(', ')}
                                </span>
                              </span>
                            );
                          })()}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{client.company || '-'}</TableCell>
                      <TableCell className="text-sm">
                        <div className="space-y-0.5">
                          {allNames.length > 0 ? allNames.map((n, i) => (
                            <div key={i} className="text-xs">{n}</div>
                          )) : '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div className="space-y-0.5">
                          {allEmails.length > 0 ? allEmails.map((e, i) => (
                            <div key={i} className="text-xs truncate max-w-[150px]" title={e}>{e}</div>
                          )) : '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="space-y-0.5">
                          {allPhones.length > 0 ? allPhones.map((p, i) => (
                            <div key={i} className="text-xs">{p}</div>
                          )) : '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div className="space-y-0.5">
                          {allCities.length > 0 ? allCities.map((c, i) => (
                            <div key={i} className="text-xs">{c}</div>
                          )) : '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div className="space-y-0.5">
                          {allStates.length > 0 ? allStates.map((s, i) => (
                            <div key={i} className="text-xs">{s}</div>
                          )) : '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div className="space-y-0.5">
                          {allCountries.length > 0 ? allCountries.map((c, i) => (
                            <div key={i} className="text-xs">{c}</div>
                          )) : '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{client.event_count || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {client.last_event_date ? formatDate(client.last_event_date) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openView(client)} data-testid={`view-client-${client.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" onClick={() => openEdit(client)} data-testid={`edit-client-${client.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedClient(client); setIsDeleteOpen(true); }} data-testid={`delete-client-${client.id}`}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Event History Tab */}
        <TabsContent value="history" className="space-y-4 mt-4">
          <Card>
            <CardContent className="p-4 md:p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Filter className="h-5 w-5 text-primary" />
                Filtros
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Cliente</Label>
                  <Select value={historyFilters.client_id} onValueChange={v => setHistoryFilters({...historyFilters, client_id: v === '_all' ? '' : v})}>
                    <SelectTrigger className="h-8 text-xs" data-testid="history-filter-client">
                      <SelectValue placeholder="Todos los clientes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">Todos los clientes</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipo</Label>
                  <Select value={historyFilters.movement_type} onValueChange={v => setHistoryFilters({...historyFilters, movement_type: v === '_all' ? '' : v})}>
                    <SelectTrigger className="h-8 text-xs" data-testid="history-filter-type">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">Todos</SelectItem>
                      <SelectItem value="salida">Salida</SelectItem>
                      <SelectItem value="retorno">Retorno</SelectItem>
                      <SelectItem value="prestamo">Prestamo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Estado</Label>
                  <SearchableSelect
                    value={historyFilters.event_state}
                    onValueChange={v => setHistoryFilters({...historyFilters, event_state: v})}
                    options={MEXICAN_STATES}
                    placeholder="Todos"
                    showManualOption={true}
                    manualLabel="Escribir manualmente"
                    size="sm"
                    data-testid="history-filter-state"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Ciudad</Label>
                  <Input
                    className="h-8 text-xs"
                    placeholder="Filtrar por ciudad"
                    value={historyFilters.event_city}
                    onChange={e => setHistoryFilters({...historyFilters, event_city: e.target.value})}
                    data-testid="history-filter-city"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Pais</Label>
                  <SearchableSelect
                    value={historyFilters.event_country}
                    onValueChange={v => setHistoryFilters({...historyFilters, event_country: v})}
                    options={COUNTRIES}
                    placeholder="Todos"
                    showManualOption={true}
                    manualLabel="Escribir manualmente"
                    size="sm"
                    data-testid="history-filter-country"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Desde</Label>
                  <Input type="date" className="h-8 text-xs" value={historyFilters.date_from} onChange={e => setHistoryFilters({...historyFilters, date_from: e.target.value})} data-testid="history-filter-from" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Hasta</Label>
                  <Input type="date" className="h-8 text-xs" value={historyFilters.date_to} onChange={e => setHistoryFilters({...historyFilters, date_to: e.target.value})} data-testid="history-filter-to" />
                </div>
                <div className="flex items-end gap-2">
                  <Button size="sm" className="h-8" onClick={() => loadEventHistory(historyFilters)} data-testid="history-filter-apply">
                    <Search className="h-3 w-3 mr-1" /> Buscar
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => {
                    const empty = { client_id: '', event_city: '', event_state: '', event_country: '', date_from: '', date_to: '', movement_type: '' };
                    setHistoryFilters(empty);
                    loadEventHistory(empty);
                  }} data-testid="history-filter-clear">
                    <X className="h-3 w-3 mr-1" /> Limpiar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <ArrowLeftRight className="h-5 w-5 text-primary" />
                  Eventos ({eventHistory.length})
                </h2>
              </div>
              {historyLoading ? (
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
                </div>
              ) : eventHistory.length === 0 ? (
                <div className="text-center py-12">
                  <ArrowLeftRight className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">No hay eventos para los filtros seleccionados</p>
                  <p className="text-xs text-muted-foreground mt-1">Ajusta los filtros o haz clic en Buscar</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {eventHistory.map(event => (
                    <div key={event.id} className="border rounded-lg p-4 space-y-3" data-testid={`history-event-${event.id}`}>
                      {/* Header */}
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={event.type === 'salida' ? 'default' : event.type === 'retorno' ? 'secondary' : 'outline'} className="text-xs">
                            {event.type === 'salida' ? 'Salida' : event.type === 'retorno' ? 'Retorno' : event.type === 'prestamo' ? 'Prestamo' : event.type}
                          </Badge>
                          <span className="font-semibold text-sm">{event.event_name || event.loan_reason || '-'}</span>
                          {event.client_name && (
                            <Badge variant="outline" className="text-xs">
                              <Building2 className="h-3 w-3 mr-1" />
                              {event.client_name}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span><Calendar className="h-3 w-3 inline mr-1" />{formatDate(event.event_date || event.created_at)}</span>
                          {event.type === 'salida' && (
                            event.has_return ? (
                              <Badge variant="outline" className="text-xs text-emerald-500 border-emerald-500/30">
                                <CheckCircle className="h-3 w-3 mr-1" /> Retornado
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">
                                <Clock className="h-3 w-3 mr-1" /> Pendiente retorno
                              </Badge>
                            )
                          )}
                        </div>
                      </div>

                      {/* Location Info */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                        {event.responsible_name && <span><User className="h-3 w-3 inline mr-1" />Responsable: <strong>{event.responsible_name}</strong></span>}
                        {event.event_city && <span><MapPin className="h-3 w-3 inline mr-1" />{[event.event_city, event.event_state, event.event_country].filter(Boolean).join(', ')}</span>}
                      </div>

                      {/* Equipment Items */}
                      {(event.items || []).length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Equipos:</p>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="text-xs h-7">Equipo</TableHead>
                                  <TableHead className="text-xs h-7">Cant.</TableHead>
                                  <TableHead className="text-xs h-7">Series</TableHead>
                                  <TableHead className="text-xs h-7">Ubicacion</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {event.items.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="text-xs py-1">{item.equipment_name || 'Equipo'}</TableCell>
                                    <TableCell className="text-xs py-1">{item.quantity}</TableCell>
                                    <TableCell className="text-xs py-1 font-mono">{item.serial_numbers || '-'}</TableCell>
                                    <TableCell className="text-xs py-1">{item.location_name || '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}

                      {/* Return Info for Salidas */}
                      {event.type === 'salida' && event.has_return && (
                        <div className="bg-muted/20 rounded p-2 space-y-1">
                          <p className="text-xs font-medium">Informacion de retorno:</p>
                          <div className="flex items-center gap-3 text-xs">
                            {event.return_date && <span><Calendar className="h-3 w-3 inline mr-1" />Retorno: {formatDate(event.return_date)}</span>}
                            {event.return_fallas > 0 && (
                              <Badge variant="outline" className="text-xs text-red-500 border-red-500/30">
                                <XCircle className="h-3 w-3 mr-1" /> {event.return_fallas} con fallas
                              </Badge>
                            )}
                            {event.return_perdidos > 0 && (
                              <Badge variant="outline" className="text-xs text-red-600 border-red-600/30">
                                <AlertTriangle className="h-3 w-3 mr-1" /> {event.return_perdidos} perdidos
                              </Badge>
                            )}
                            {event.return_fallas === 0 && event.return_perdidos === 0 && (
                              <Badge variant="outline" className="text-xs text-emerald-500 border-emerald-500/30">
                                <CheckCircle className="h-3 w-3 mr-1" /> Todo en buen estado
                              </Badge>
                            )}
                          </div>
                          {(event.return_items || []).length > 0 && (event.return_fallas > 0 || event.return_perdidos > 0) && (
                            <div className="mt-1">
                              {event.return_items.filter(it => it.condition && it.condition !== 'bueno').map((it, idx) => (
                                <div key={idx} className="text-xs flex items-center gap-2">
                                  <Badge variant={it.condition === 'falla' ? 'destructive' : 'outline'} className="text-xs">
                                    {it.condition === 'falla' ? 'Falla' : it.condition === 'perdido' ? 'Perdido' : it.condition}
                                  </Badge>
                                  <span>{it.equipment_name} x{it.quantity}</span>
                                  {it.serial_numbers && <span className="font-mono text-muted-foreground">({it.serial_numbers})</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {event.notes && (
                        <p className="text-xs text-muted-foreground italic">Notas: {event.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Cliente</DialogTitle>
            <DialogDescription>Registra un nuevo cliente en el sistema</DialogDescription>
          </DialogHeader>
          {clientFormFields}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-client-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Crear Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
            <DialogDescription>Actualiza la informacion del cliente</DialogDescription>
          </DialogHeader>
          {clientFormFields}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancelar</Button>
            <Button onClick={handleEdit} disabled={saving} data-testid="update-client-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog with Event History */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              {selectedClient?.name}
            </DialogTitle>
            <DialogDescription>{selectedClient?.company}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {selectedClient && (
              <div className="space-y-6 pr-4">
                {/* Client Info */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedClient.contact_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedClient.contact_name}</span>
                    </div>
                  )}
                  {selectedClient.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedClient.email}</span>
                    </div>
                  )}
                  {selectedClient.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedClient.phone}</span>
                    </div>
                  )}
                  {(selectedClient.city || selectedClient.state) && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{[selectedClient.city, selectedClient.state].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                </div>

                {/* Additional Contacts */}
                {(selectedClient.contacts || []).length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Contactos Adicionales</h4>
                    <div className="space-y-2">
                      {selectedClient.contacts.map((ct, idx) => (
                        <div key={idx} className="flex items-center gap-4 text-sm p-2 bg-muted/20 rounded">
                          {ct.name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{ct.name}</span>}
                          {ct.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{ct.email}</span>}
                          {ct.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{ct.phone}</span>}
                          {ct.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{ct.city}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedClient.address && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedClient.address}</span>
                  </div>
                )}
                {selectedClient.notes && (
                  <div className="p-3 bg-muted/30 rounded-lg text-sm">
                    <strong>Notas:</strong> {selectedClient.notes}
                  </div>
                )}

                <Separator />

                {/* Event History */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <ArrowLeftRight className="h-4 w-4" />
                    Historial de Eventos ({clientEvents.length})
                  </h3>
                  {eventsLoading ? (
                    <div className="space-y-2">
                      {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                    </div>
                  ) : clientEvents.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      Este cliente no tiene eventos registrados
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {clientEvents.map(event => (
                        <div key={event.id} className="border rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant={event.type === 'salida' ? 'default' : event.type === 'retorno' ? 'secondary' : 'outline'}>
                                {event.type === 'salida' ? 'Salida' : event.type === 'retorno' ? 'Retorno' : event.type}
                              </Badge>
                              <span className="font-medium text-sm">{event.event_name || event.loan_reason || '-'}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3 inline mr-1" />
                              {formatDate(event.event_date || event.created_at)}
                            </span>
                          </div>
                          {event.responsible_name && (
                            <p className="text-xs text-muted-foreground">
                              Responsable: <strong>{event.responsible_name}</strong>
                            </p>
                          )}
                          {event.items && event.items.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {event.items.map((item, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  <Package className="h-3 w-3 mr-1" />
                                  {item.equipment_name || 'Equipo'} x{item.quantity}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Eliminar Cliente
            </DialogTitle>
            <DialogDescription>
              Estas seguro de eliminar a <strong>{selectedClient?.name}</strong>? Esta accion no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-client-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DuplicateWarningDialog
        duplicateError={duplicateError}
        onCancel={() => setDuplicateError(null)}
        onForceCreate={handleForceCreateClient}
        entityLabel="cliente"
      />
    </div>
  );
};

export default ClientsPage;
